import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@api/client';
import { useNotificationStore } from '@store/notificationStore';
import { useAuthStore } from '@store/authStore';
import type {
  ErpFieldCatalogResponse,
  ErpFieldOptionsResponse,
  TenantErpFieldConfig,
  TenantErpFieldConfigItem,
} from '@schemas/api';

const useTenantQueryScope = () => useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

export const useErpFieldCatalog = (surface?: string, enabled = true, erpConnectionId?: string) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['erp-fields', tenantScope, erpConnectionId ?? 'default', 'catalog', surface],
    queryFn: async () => {
      const { data } = await apiClient.get<ErpFieldCatalogResponse>('/erp/fields/catalog', {
        params: { surface, erp_connection_id: erpConnectionId },
      });
      return data;
    },
    enabled,
  });
};

export const useErpFieldConfig = (surface?: string, enabled = true, erpConnectionId?: string) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['erp-fields', tenantScope, erpConnectionId ?? 'default', 'config', surface],
    queryFn: async () => {
      const { data } = await apiClient.get<TenantErpFieldConfig[]>('/erp/fields/config', {
        params: { surface, erp_connection_id: erpConnectionId },
      });
      return data;
    },
    enabled,
  });
};

export const useUpdateErpFieldConfig = (erpConnectionId?: string) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (fields: TenantErpFieldConfigItem[]) => {
      const { data } = await apiClient.patch<TenantErpFieldConfig[]>(
        '/erp/fields/config',
        { fields },
        { params: { erp_connection_id: erpConnectionId } },
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['erp-fields'] });
      addNotification({ message: 'ERP field configuration saved', type: 'success' });
    },
    onError: () => {
      addNotification({ message: 'Failed to save ERP field configuration', type: 'error' });
    },
  });
};

export const useErpFieldOptions = (
  params: {
    field_definition_id?: string;
    field_key?: string;
    object_type?: string;
    erp_connection_id?: string;
    search?: string;
    skip?: number;
    limit?: number;
  },
  enabled = true,
) => {
  const tenantScope = useTenantQueryScope();

  return useQuery({
    queryKey: ['erp-fields', tenantScope, params.erp_connection_id ?? 'default', 'options', params],
    queryFn: async () => {
      const { data } = await apiClient.get<ErpFieldOptionsResponse>('/erp/fields/options', {
        params,
      });
      return data;
    },
    enabled,
  });
};
